import {Injectable} from '@angular/core';
import {BehaviorSubject, filter} from "rxjs";
import {NavItem} from "@core/models/navitem";
import {IconSmsNotification} from "@components/icons/icon-sms-notification/icon-sms-notification";
import {NavigationEnd, Router} from "@angular/router";
import {CustomMenuItem} from "@core/models/custom-menuitem";
import {IconAddComponent} from "@components/icons/icon-add/icon-add.component";
import {IconBoldEdit} from "@components/icons/icon-bold-edit/icon-bold-edit";
import {IconHomeTrendUpComponent} from "@components/icons/icon-home-trend-up/icon-home-trend-up.component";
import {IconBoldClock} from "@components/icons/icon-bold-clock/icon-bold-clock";

@Injectable({
    providedIn: 'root'
})
export class SidebarService {
    // Rotas que aparecem no sidebar
    private _items: NavItem[] = [
        {
            route: '/controle-notificacoes',
            icon: IconSmsNotification,
            name: 'Controle de Notificações'
        },
        {
            route: '/meta-bonificacao',
            icon: IconHomeTrendUpComponent,
            name: 'Meta de Bonificação'
        },
        {
            route: '/verificar-dados',
            icon: IconBoldClock,
            name: 'Histórico'
        }
    ]
    // Rotas que só aparecem no breadcrumb, não no sidebar
    private onlyBreadcrumbs: Record<string, Partial<CustomMenuItem>> = {
        '/controle-notificacoes/nova-notificacao': {
            label: 'Nova Notificação',
            iconComponent: IconAddComponent
        },
        '/controle-notificacoes/notificacao/:id': {
            label: 'Editar Notificação',
            iconComponent: IconBoldEdit
        }
    };


    constructor(private router: Router) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const currentUrl = event.urlAfterRedirects || event.url;
                const match = this._items.find(item => currentUrl.startsWith(item.route));
                this._activeItem.next(match ?? null);
            });
    }

    private _activeItem: BehaviorSubject<NavItem | null> = new BehaviorSubject<NavItem | null>(null);

    get activeItem(): NavItem | null {
        return this._activeItem.value;
    }

    get items(): NavItem[] {
        return this._items;
    }

    get breadcrumbs(): CustomMenuItem[] {
        const currentUrl = this.router.url;

        // 1. Procura por match exato
        let matchedKey = Object.keys(this.onlyBreadcrumbs).find(
            key => key === currentUrl
        );

        // 2. Procura match com rotas que possuem parâmetros (:algumaCoisa)
        if (!matchedKey) {
            matchedKey = Object.keys(this.onlyBreadcrumbs).find(key => {
                if (key.includes(':')) {
                    // Transforma cada ":param" em regex para capturar qualquer segmento não vazio
                    const regexPattern = '^' + key.replace(/:[^/]+/g, '[^/]+') + '$';
                    return new RegExp(regexPattern).test(currentUrl);
                }
                return false;
            });
        }

        // 3. Se encontrou rota no onlyBreadcrumbs
        if (matchedKey) {
            const child = this.onlyBreadcrumbs[matchedKey];
            const parentItem = this._items.find(item =>
                currentUrl.startsWith(item.route)
            );

            const trail: CustomMenuItem[] = [];
            if (parentItem) {
                trail.push({
                    label: parentItem.name,
                    routerLink: [parentItem.route],
                    url: parentItem.route,
                    iconComponent: parentItem.icon
                });
            }

            trail.push({
                label: child.label,
                routerLink: [currentUrl],
                url: currentUrl,
                iconComponent: child.iconComponent ?? null
            });

            return trail;
        }

        // 4. Fallback padrão (sidebar ativo)
        const trail: CustomMenuItem[] = [];
        let current = this._activeItem.value;

        while (current) {
            trail.unshift({
                label: current.name,
                routerLink: [current.route],
                url: current.route,
                iconComponent: current.icon
            });
            current = current.parent ?? null;
        }

        return trail;
    }


    get activeItem$() {
        return this._activeItem.asObservable();
    }

    setActiveItem(activeItem: NavItem) {
        this._activeItem.next(activeItem);
    }
}
